import * as React from 'react';
import { SortableElement, SortableHandle } from 'react-sortable-hoc';
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import RichTextTranslation from '../../common/richTextField/Readonly';
import { flatQuestionTypes } from '@nexxt/common/questionTypes';
import { getRichTextFromQuestionTexts } from '../../../utils/question';
import {
    QTYPES,
    HealthProblemContext,
    Project,
    QuestionSummary,
    QuestionID
} from '@nexxt/common/types';
import HealthIcon from '../../common/designElements/HealthIcon';
import Icons from '../../common/designElements/Icons';
import QuestionAddButton from './common/QuestionAddButton';
import { PROJECT_EDIT_MODES } from '../../../interface/project.interface';
import { LOCK_PROJECT } from '../../../constants/project';
const QuestionTypesObject = {};
flatQuestionTypes.forEach((q) => {
    QuestionTypesObject[q.value] = q;
});

export interface CardProps {
    questionSummary: QuestionSummary;
    parentQuestion?: QuestionSummary;
    position: number;
    selected: boolean;
    languages: any[];
    moveCard: (
        dragIndex: number,
        hoverIndex: number,
        groupQuestionId: number,
        collection: number
    ) => void;
    onSelectQuestion: (
        questionSummary: QuestionSummary,
        forceParent?: boolean
    ) => void;
    actions: {
        // edit: (question: any) => void;
        // preview: (question: any) => void;
        delete: (question: any) => void;
        // logic: (question: any) => void;
        duplicate: (question: any) => void;
    };
    dragStop: () => void;
    editEnabled: boolean;
    health?: any;
    activeMenuID: number;
    onAdd: (addPosition?: number, parent?: QuestionID) => void;
    toggleActiveMenuID: (id: QuestionID) => void;
    isBottom?: boolean;
    currentProject: Project;
    editMode: PROJECT_EDIT_MODES;
    initialSelectedQuestionID: number;
    showError: boolean;
}
interface CardState {
    showAddQuestionButton: boolean;
    disabled: boolean;
}
class Card extends React.Component<CardProps, CardState> {
    scrollDiv: React.RefObject<HTMLInputElement>;
    constructor(props) {
        super(props);
        this.state = {
            showAddQuestionButton: false,
            disabled: false
        };
        // Responsible for scrolling to selected question on page load
        this.scrollDiv = React.createRef<HTMLInputElement>();
    }

    componentDidMount() {
        this.getTemplateSettings();
        if (
            this.props.initialSelectedQuestionID ===
            this.props.questionSummary.id
        ) {
            // Scrolls to selected question on page load
            this.scrollDiv.current.scrollIntoView({ behavior: 'smooth' });
        }
    }
    componentDidUpdate(prevProps) {
        if (this.props.editEnabled !== prevProps.editEnabled) {
            this.getTemplateSettings();
        }
    }
    getTemplateSettings = () => {
        const { editMode, currentProject, questionSummary } = this.props;
        try {
            const templateSettings = LOCK_PROJECT[
                editMode as PROJECT_EDIT_MODES
            ][currentProject?.templateCode]?.questions?.find(
                (q) => q.title === questionSummary.label
            );
            this.setState({ disabled: !templateSettings });
        } catch (err) {
            this.setState({ disabled: !this.props.editEnabled });
        }
    };

    onClickMenuItem = (e, actionType) => {
        e.stopPropagation();
        this.props.toggleActiveMenuID(-1);
        this.props.actions[actionType](this.props.questionSummary);
    };
    handleMouseOverAddQuestionButton = (event, show) => {
        event.stopPropagation(); // don't let the event recurse into children

        this.setState({
            showAddQuestionButton: show
        });
    };

    addNewQuestion = (e, position) => {
        e.stopPropagation();
        this.props.onAdd(position, this.props.questionSummary.parent);
    };

    render() {
        const {
            editEnabled,
            questionSummary,
            languages,
            parentQuestion,
            health,
            isBottom,
            selected,
            showError
        } = this.props;
        const { showAddQuestionButton, disabled } = this.state;
        const isBuiltIn =
            !!questionSummary.builtInID && questionSummary.isAudience;
        const enableQuestionAddButton =
            !isBuiltIn && parentQuestion?.type !== QTYPES.POPUP;
        const status = editEnabled ? '' : 'disabled';
        const greetingText = getRichTextFromQuestionTexts(
            questionSummary,
            languages
        );
        const disableDelete =
            parentQuestion?.type === QTYPES.POPUP &&
            questionSummary.type === QTYPES.MULTIMEDIA;
        const hideMenu =
            (parentQuestion?.type === QTYPES.POPUP &&
                questionSummary?.type === QTYPES.MULTIMEDIA) ||
            !editEnabled;
        const conditionProb = [],
            questionProb = [];
        const forceRenderParent =
            parentQuestion?.type === QTYPES.POPUP &&
            questionSummary?.type === QTYPES.MULTIMEDIA;
        const hideHealthIconForNewlyAddedQuestion = selected && !showError;
        health?.list.forEach((problem) =>
            problem.context === HealthProblemContext.CONDITION
                ? conditionProb.push(problem)
                : questionProb.push(problem)
        );
        return (
            <div
                className={classnames(`question-card`, {
                    active: this.props.selected,
                    disabled
                })}
                onClick={() => {
                    this.props.toggleActiveMenuID(-1);
                    this.props.onSelectQuestion(
                        questionSummary,
                        forceRenderParent
                    );
                }}
                ref={this.scrollDiv}
            >
                {editEnabled && enableQuestionAddButton && (
                    <QuestionAddButton
                        questionID={questionSummary.id}
                        onMouseEvent={this.handleMouseOverAddQuestionButton}
                        addQuestion={(e) =>
                            this.addNewQuestion(e, questionSummary.position)
                        }
                        show={showAddQuestionButton}
                    />
                )}
                {this.props.activeMenuID === questionSummary.id && (
                    <div
                        className="q-card-menu"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!questionSummary.isAudience &&
                            !questionSummary.parent && (
                                <p
                                    className="q-card-menu--item"
                                    onClick={(e) =>
                                        this.onClickMenuItem(e, 'duplicate')
                                    }
                                >
                                    Duplicate
                                </p>
                            )}
                        <p
                            className="q-card-menu--item"
                            onClick={(e) => this.onClickMenuItem(e, 'delete')}
                        >
                            Delete
                        </p>
                    </div>
                )}
                {editEnabled && <DragHandle />}
                <div className="q-card-pre-header">
                    {!!questionSummary.hasCondition && (
                        <span className="pre-header-icon logic">LOGIC</span>
                    )}
                    {!!questionSummary.hasCustomLogic && (
                        <span className="pre-header-icon logic">CUSTOM</span>
                    )}
                    {questionSummary.isAudience && (
                        <>
                            <span
                                className="pre-header-icon demographic"
                                data-tip="custom show"
                                data-for={`demographic-${questionSummary.id}`}
                            >
                                <Icons type="demographic" width={20} />
                            </span>
                            <ReactTooltip
                                className="pre-header-tooltip"
                                id={`demographic-${questionSummary.id}`}
                                aria-haspopup="true"
                                effect="solid"
                                place="right"
                                arrowColor={'transparent'}
                            >
                                <span>DEMOGRAPHIC</span>
                            </ReactTooltip>
                        </>
                    )}
                    {!hideMenu ? (
                        <span
                            className="pre-header-icon menu"
                            onClick={(e) => {
                                e.stopPropagation();
                                this.props.toggleActiveMenuID(
                                    questionSummary.id
                                );
                            }}
                        >
                            <Icons type="eclipsis-h" />
                        </span>
                    ) : null}
                </div>

                <div
                    className={classnames(
                        `q-card-header q-card-header--single-question`,
                        {
                            'logic-label':
                                !!questionSummary.hasCondition &&
                                !questionSummary.isAudience,
                            'audience-label':
                                !questionSummary.hasCondition &&
                                questionSummary.isAudience,
                            'logic-audience-label':
                                !!questionSummary.hasCondition &&
                                questionSummary.isAudience
                        }
                    )}
                >
                    {!(
                        questionSummary.type === QTYPES.POPUP ||
                        questionSummary.type === QTYPES.POPUP_CARD
                    ) && (
                        <span className="question-image">
                            <img
                                src={
                                    (
                                        QuestionTypesObject[
                                            questionSummary.type
                                        ] ||
                                        QuestionTypesObject[
                                            QTYPES.SINGLE_CHOICE
                                        ]
                                    ).image
                                }
                            />
                        </span>
                    )}
                    <span className="question-label">
                        {questionSummary.label || questionSummary.title}
                    </span>
                    {!hideHealthIconForNewlyAddedQuestion && (
                        <HealthIcon
                            questionID={questionSummary.id}
                            active={!!health}
                            type={health?.type}
                            className={`health ${health?.type?.toLowerCase()}`}
                            message={health && health.list[0].message}
                        />
                    )}
                </div>
                {!isBuiltIn && (
                    <>
                        <div className="q-card-body">
                            <div className="card-content">
                                <RichTextTranslation
                                    value={greetingText}
                                    isString={isBuiltIn}
                                />
                            </div>
                        </div>
                    </>
                )}
                {/** Enable add new question button at the bottom of the question card only if it has a parent question*/}
                {editEnabled &&
                    isBottom &&
                    enableQuestionAddButton &&
                    parentQuestion && (
                        <QuestionAddButton
                            questionID={questionSummary.id}
                            onMouseEvent={this.handleMouseOverAddQuestionButton}
                            addQuestion={(e) =>
                                this.addNewQuestion(
                                    e,
                                    questionSummary.position + 1
                                )
                            }
                            show={showAddQuestionButton}
                            bottom={isBottom}
                        />
                    )}
            </div>
        );
    }
}
const DragHandle = SortableHandle(() => <div className="q-card-drag"></div>);

export default SortableElement(Card);
